import type { BulkInboundFailure, BulkInboundResponse } from '@/infrastructure/FulfillmentAdminClient';
import type { FulfillmentToastState } from './useFulfillmentToast';

export interface BulkItemReason {
  item: string;
  reason: string;
}

const STATUS_REASONS: Record<string, string> = {
  INBOUNDED: 'Already inbounded',
  CANCELLED: 'Cancelled',
  OUTBOUNDED: 'Already outbounded',
};

const displayReason = (reason: string) => STATUS_REASONS[reason] ?? reason;

const reasonSuffix = (items: BulkItemReason[]): string => {
  const counts = new Map<string, number>();
  for (const { reason } of items) {
    // The plan keeps this sentence in item details; grouped counts do not need its full stop.
    const text =
      reason === 'No longer eligible for inbound.' ? 'No longer eligible for inbound' : displayReason(reason);
    counts.set(text, (counts.get(text) ?? 0) + 1);
  }
  return counts.size ? ` (${[...counts].map(([reason, count]) => `${reason}: ${count}`).join('; ')})` : '';
};

/** Counts name items, not their quantities. Exclusions and refusals never share a bucket. */
export const formatBulkResult = (
  action: string,
  processed: number,
  excluded: BulkItemReason[] = [],
  failed: BulkItemReason[] = [],
  suffix?: string,
): FulfillmentToastState => ({
  tone: failed.length > 0 && processed === 0 ? 'error' : excluded.length || failed.length ? 'warning' : 'success',
  text: `${action}: ${processed} processed · ${excluded.length} excluded${reasonSuffix(excluded)} · ${failed.length} failed${reasonSuffix(failed)}.${suffix ? ` ${suffix}` : ''}`,
  details: [...excluded, ...failed].map(({ item, reason }) => `${item} — ${displayReason(reason)}`),
});

export const UNCONFIRMED_BULK_RESULT = 'Could not confirm the bulk result. Reload before retrying.';
export const NOT_ATTEMPTED = 'Not attempted after an earlier failure';

/** A lost response is not a refusal. Preserve prior receipts without guessing the current item's outcome. */
export const unconfirmedSequentialResult = (
  action: string,
  items: string[],
  processed: number,
): FulfillmentToastState => ({
  tone: 'error',
  text: UNCONFIRMED_BULK_RESULT,
  details: [
    ...(processed > 0
      ? [`${action}: ${processed} processed.`, ...items.slice(0, processed).map((item) => `${item} — Processed.`)]
      : []),
    ...(items[processed] ? [`${items[processed]} — ${UNCONFIRMED_BULK_RESULT}`] : []),
    ...items.slice(processed + 1).map((item) => `${item} — ${NOT_ATTEMPTED}`),
  ],
});

/** The shelves a walk that continues past refusals had reached when one answer was lost. */
export interface InterruptedSequence {
  /** Items the server confirmed before the loss. */
  confirmed: string[];
  /** Items the server refused before the loss, each with its reason; they stand as failures, not as processed. */
  failed: BulkItemReason[];
  /** The item whose answer was lost: whether it was processed is unknown. */
  unknown: string;
  /** Items after it, never sent. */
  notAttempted: string[];
}

/**
 * A lost response in a sequence that keeps going past refusals [D8]: the confirmed items are reported as
 * such, the refused ones keep their reasons rather than being counted as processed, the one with the lost
 * answer is marked unknown, and the rest were never attempted.
 */
export const interruptedSequenceResult = (action: string, sequence: InterruptedSequence): FulfillmentToastState => ({
  tone: 'error',
  text: UNCONFIRMED_BULK_RESULT,
  details: [
    ...(sequence.confirmed.length > 0
      ? [
          `${action}: ${sequence.confirmed.length} processed.`,
          ...sequence.confirmed.map((item) => `${item} — Processed.`),
        ]
      : []),
    ...sequence.failed.map(({ item, reason }) => `${item} — ${displayReason(reason)}`),
    `${sequence.unknown} — ${UNCONFIRMED_BULK_RESULT}`,
    ...sequence.notAttempted.map((item) => `${item} — ${NOT_ATTEMPTED}`),
  ],
});

type CompleteBulkInboundResponse = BulkInboundResponse & {
  skipped: BulkInboundFailure[];
  failures: BulkInboundFailure[];
};

/** Only complete, unique per-line metadata can prove which requested lines were applied. */
export const isCompleteInboundBulkResult = (
  result: BulkInboundResponse,
  requested: number[],
): result is CompleteBulkInboundResponse => {
  if (
    !Number.isInteger(result?.inbounded) ||
    result.inbounded < 0 ||
    !Array.isArray(result.skipped) ||
    !Array.isArray(result.failures)
  ) {
    return false;
  }
  const outcomes = [...result.skipped, ...result.failures];
  const ids = outcomes.map((outcome) => outcome?.lineNo);
  return (
    new Set(requested).size === requested.length &&
    new Set(ids).size === ids.length &&
    outcomes.every(
      (outcome) =>
        outcome &&
        requested.includes(outcome.lineNo) &&
        typeof outcome.reason === 'string' &&
        outcome.reason.trim().length > 0,
    ) &&
    result.inbounded + ids.length === requested.length
  );
};

/** Only complete, unique per-line metadata can prove which selected lines were applied. */
export const inboundBulkResult = (
  result: BulkInboundResponse,
  requested: number[],
  clientExcluded: number[] = [],
): { notification: FulfillmentToastState; applied: number[] } => {
  if (!Number.isInteger(result?.inbounded) || result.inbounded < 0) {
    return { notification: { tone: 'error', text: UNCONFIRMED_BULK_RESULT }, applied: [] };
  }
  const skipped = Array.isArray(result.skipped) ? result.skipped : [];
  const failures = Array.isArray(result.failures) ? result.failures : [];
  const ids = [...skipped, ...failures].map((outcome) => outcome?.lineNo);
  if (!isCompleteInboundBulkResult(result, requested)) {
    return {
      notification: {
        tone: 'warning',
        text: `Bulk Inbound: ${result.inbounded} processed. Some item outcomes were not returned; reload before retrying.`,
      },
      applied: [],
    };
  }
  const excluded = [
    ...clientExcluded
      .filter((lineNo) => !requested.includes(lineNo))
      .map((lineNo) => ({ item: `Line ${lineNo}`, reason: 'No longer eligible for inbound.' })),
    ...skipped.map(({ lineNo, reason }) => ({ item: `Line ${lineNo}`, reason })),
  ];
  return {
    notification: formatBulkResult(
      'Bulk Inbound',
      result.inbounded,
      excluded,
      failures.map(({ lineNo, reason }) => ({ item: `Line ${lineNo}`, reason })),
    ),
    applied: requested.filter((lineNo) => !ids.includes(lineNo)),
  };
};
